<template>
  <div class="book-page" :style="{ backgroundImage: `url(${designUrl}${isMobile ? 'AddPetPage_phone.png' : 'AddPetPage.jpeg'})` }">
    <div class="sidebar-wrapper">
      <button class="hamburger" @click="sidebarOpen = !sidebarOpen">☰</button>
      <div class="sidebar" :class="{ open: sidebarOpen }">
        <div class="sidebar-header">
          <button class="close-btn" @click="sidebarOpen = false">✖</button>
          <p>{{ user.fullname }}</p>
          <p>{{ user.phone_number }}</p>
        </div>
        <ul class="sidebar-menu">
          <template v-if="user && user.user_type === 'Pet Owner'">
            <li @click="goToPage('DashboardPage')">Browse Pets</li>
            <li class="has-submenu" @click="petsOpen = !petsOpen">
              My Pets
              <span class="arrow" :class="{ 'arrow-open': petsOpen }">›</span>
            </li>
            <ul class="sub-menu" :class="{ 'sub-open': petsOpen }">
              <li @click="goToPage('YourPetsPage')">View My Pets</li>
              <li @click="goToPage('AddPetPage')">Add Pet</li>
              <li @click="goToPage('MeetingsPage')">Booking Requests</li>
            </ul>
            <li @click="goToPage('EditUserDashboard')">My Profile</li>
            <li @click="goToPage('LogoutPage')">Log Out</li>
          </template>
          <template v-else>
            <li @click="goToPage('DashboardPage')">Browse Pets</li>
            <li @click="goToPage('CalendarPage')">My Calendar</li>
            <li @click="goToPage('EditUserDashboard')">My Profile</li>
            <li @click="goToPage('LogoutPage')">Log Out</li>
          </template>
        </ul>
      </div>
    </div>

    <div class="page-header">
      <h1 class="page-title" v-if="pet">Book with {{ pet.pet_name }}</h1>
      <h1 class="page-title" v-else>Loading...</h1>
      <p class="page-sub">Pick a date and time slot</p>
    </div>

    <div class="calendar-button-wrapper">
      <button @click="goToCalendar" class="calendar-button">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2"></rect>
          <line x1="16" y1="2" x2="16" y2="6"></line>
          <line x1="8" y1="2" x2="8" y2="6"></line>
          <line x1="3" y1="10" x2="21" y2="10"></line>
        </svg>
      </button>
    </div>

    <div class="page-wrapper">
      <div v-if="loadingPet" class="loading-wrapper">
        <div class="loading-spinner"></div>
        <p class="loading-label">Loading pet details...</p>
      </div>

      <div v-else-if="!pet" class="loading-wrapper">
        <p class="loading-label">Could not load pet details. Please go back and try again.</p>
      </div>

      <div class="form-card" v-else>
        <div class="form-group">
          <div class="booking-notice" v-if="pet">
            You can book an playdate with <b>{{ pet.pet_name }}</b> within 1 week from today.
          </div>
          <label class="form-label">Date</label>
          <input
            class="form-input"
            type="date"
            v-model="date"
            :min="minDate"
            :max="maxDate"
            @change="loadBookedSlots"
          />
        </div>

        <div class="form-group" v-if="date">
          <label class="form-label">Time Slot</label>
          <div class="slots">
            <button
              v-for="hour in hours"
              :key="hour"
              class="slot-btn"
              :disabled="booked.includes(hour)"
              :class="{ 'slot-selected': time === hour, 'slot-booked': booked.includes(hour) }"
              @click="time = hour"
            >{{ formatTime(hour) }}</button>
          </div>
        </div>

        <div v-if="!date" class="date-prompt">
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#d8a0ff" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2"></rect>
            <line x1="16" y1="2" x2="16" y2="6"></line>
            <line x1="8" y1="2" x2="8" y2="6"></line>
            <line x1="3" y1="10" x2="21" y2="10"></line>
          </svg>
          <p>Select a date to see available slots</p>
        </div>

        <button
          class="submit-btn"
          :disabled="!date || !time || bookingSuccess || bookingLoading"
          :class="{ 'submit-success': bookingSuccess }"
          @click="submitBooking"
        >
          <span v-if="bookingSuccess">✓ Booked!</span>
          <span v-else-if="bookingLoading" class="btn-spinner-wrap">
            <span class="btn-spinner"></span>
            Booking...
          </span>
          <span v-else>{{ time ? `Book at ${formatTime(time)}` : 'Select a time slot' }}</span>
        </button>

        <p v-if="message" class="message" :class="message.includes('Error') || message.includes('Please') ? 'message-error' : 'message-success'">
          {{ message }}
        </p>

        <button v-if="bookingSuccess" class="browse-btn" @click="goToPage('DashboardPage')">
          Browse More Pets
        </button>
      </div>
    </div>
  </div>
</template>

<script>
const backendUrl = process.env.VUE_APP_API_URL;

export default {
  props: {
    petId: { type: [String, Number], required: true },
    user: { type: Object, default: () => ({}) }
  },
  data() {
    return {
      date: '',
      time: '',
      message: '',
      pet: null,
      booked: [],
      sidebarOpen: false,
      bookingSuccess: false,
      petsOpen: false,
      loadingPet: true,
      bookingLoading: false,
      designUrl: '/background_designs/',
      isMobile: window.innerWidth <= 480,
    }
  },
  created() {
    // use created() so loadingPet:true is set BEFORE first render
    this.loadingPet = true;
    const minDelay = new Promise(resolve => setTimeout(resolve, 1200));
    const fetchPet = fetch(`${backendUrl}/api/pets/${this.petId}`, { credentials: 'include' })
      .then(res => res.json())
      .then(data => { this.pet = (data && data.id) ? data : null; })
      .catch(() => { this.pet = null; });
    Promise.all([fetchPet, minDelay]).finally(() => {
      this.loadingPet = false;
    });
  },
  computed: {
    hours() {
      if (!this.pet) return [];
      const start = this.pet.available_start_time;
      const end = this.pet.available_end_time;
      if (!start || !end) return [];
      const [sh] = start.split(':').map(Number);
      const [eh] = end.split(':').map(Number);
      const arr = [];
      for (let h = sh; h <= eh; h++) {
        arr.push(String(h).padStart(2, '0') + ':00:00');
      }
      return arr;
    },
    minDate() { return new Date().toISOString().split('T')[0]; },
    maxDate() {
      const d = new Date();
      d.setDate(d.getDate() + 7);
      return d.toISOString().split('T')[0];
    }
  },
  methods: {
    goToCalendar() {
      this.$emit('change-page', 'CalendarPage');
    },
    goToPage(pageName) {
      this.sidebarOpen = false;
      this.$emit('change-page', pageName);
    },
    isTimeBetween(time, start, end) {
      const toMinutes = t => {
        const [h, m, s = "0"] = t.split(':').map(Number);
        return h * 60 + m + s / 60;
      }
      return toMinutes(time) >= toMinutes(start) && toMinutes(time) <= toMinutes(end);
    },
    async submitBooking() {
      this.message = '';
      if (!this.date) { this.message = 'Please select a date.'; return; }
      const selectedDate = new Date(this.date);
      if (selectedDate < new Date(this.minDate) || selectedDate > new Date(this.maxDate)) {
        this.message = `Please pick a date between ${this.minDate} and ${this.maxDate}.`;
        return;
      }
      if (!this.time) { this.message = 'Please select a time.'; return; }
      if (!this.pet) { this.message = 'Pet data not loaded.'; return; }
      if (!this.isTimeBetween(this.time, this.pet.available_start_time, this.pet.available_end_time)) {
        this.message = `Please pick a time between ${this.pet.available_start_time} and ${this.pet.available_end_time}.`;
        return;
      }

      this.bookingLoading = true; // ← start spinner

      try {
        const response = await fetch(`${backendUrl}/api/meetings/book-appointment`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ pet_id: this.petId, date: this.date, time: this.time })
        });
        const data = await response.json();
        if (data.message) {
          if (response.ok) {
            this.bookingSuccess = true;
          } else {
            this.message = data.message || 'Booking failed.';
          }
          this.loadBookedSlots();
        } else {
          this.message = data.error || 'Booking failed.';
        }
      } catch (err) {
        this.message = 'Error booking appointment.';
      } finally {
        this.bookingLoading = false; // ← stop spinner
      }
    },
    async loadBookedSlots() {
      if (!this.date) return;
      try {
        const res = await fetch(`${backendUrl}/api/meetings/booked-slots?pet_id=${this.petId}&date=${this.date}`, { credentials: 'include' });
        const data = await res.json();
        this.booked = data.booked || [];
      } catch (err) {
        this.message = 'Error loading booked slots.';
      }
    },
    handleResize() {
      this.isMobile = window.innerWidth <= 480;
    },
    formatTime(timeStr) {
      if (!timeStr) return '';
      let [h, m] = timeStr.split(':').map(Number);
      const ampm = h >= 12 ? 'PM' : 'AM';
      h = h % 12 || 12;
      return `${h}:${m.toString().padStart(2, '0')} ${ampm}`;
    },
  },
  mounted() {
    window.addEventListener('resize', this.handleResize);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
  },
}
</script>

<style scoped>

@keyframes bookPageSpin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-spinner {
  width: 48px;
  height: 48px;
  border: 5px solid #f0e0ff;
  border-top-color: #ba71ff;
  border-radius: 50%;
  animation: bookPageSpin 0.75s linear infinite;
  display: block;
}

.btn-spinner-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.btn-spinner {
  width: 18px;
  height: 18px;
  border: 3px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: bookPageSpin 0.6s linear infinite;
  display: inline-block;
  flex-shrink: 0;
}

* { margin: 0; padding: 0; box-sizing: border-box; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }

.book-page {
  min-height: 100vh;
  background-size: cover;
  background-position: center center;
  background-repeat: no-repeat;
  background-attachment: scroll;
  background-color: #f5ece0;
}

.page-header {
  max-width: 1200px;
  margin: 0 auto;
  padding: 28px 20px 0 24px;
}

.page-title {
  font-size: 22px;
  font-weight: 800;
  color: #1a1a1a;
  letter-spacing: -0.4px;
  margin: 0;
  margin-top: 8px; margin-left: 7px;
}

.page-sub {
  font-size: 13px;
  color: #aaa;
  font-weight: 500;
  margin-top: 2px;
}

.page-wrapper {
  max-width: 960px;
  margin: 0 auto;
  padding: 14px 24px 40px;
}

.loading-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 60px 20px;
}

.loading-label {
  font-size: 14px;
  color: #aaa;
  font-weight: 500;
}

.form-card {
  background: #ffffff;
  border-radius: 20px;
  border: 1px solid #f0e0ff;
  padding: 20px 26px;
  box-shadow: 0 4px 24px rgba(186, 113, 255, 0.08);
}

.form-group {
  margin-bottom: 14px;
}

.form-label {
  display: block;
  font-size: 11px;
  font-weight: 700;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 5px;
}

.form-input {
  width: 100%;
  padding: 9px 13px;
  border: 1.5px solid #f0e0ff;
  border-radius: 11px;
  font-size: 14px;
  background: #fffbe8;
  outline: none;
  transition: all 0.2s ease;
  color: #1a1a1a;
  font-family: inherit;
}

.form-input:focus {
  border-color: #ba71ff;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(186, 113, 255, 0.15);
}

.slots {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
}

.slot-btn {
  padding: 10px 6px;
  border: 1.5px solid #f0e0ff;
  border-radius: 11px;
  background: #fffbe8;
  font-size: 13px;
  font-weight: 700;
  color: #4a1070;
  cursor: pointer;
  transition: all 0.18s ease;
  font-family: inherit;
  width: 100%;
}

.slot-btn:hover:not(:disabled) { border-color: #ba71ff; background: #eac7ff; }
.slot-btn.slot-selected { background: #eac7ff; border-color: #ba71ff; color: #4a1070; box-shadow: 0 2px 8px rgba(186,113,255,0.25); }
.slot-btn.slot-booked { background: #f5f5f5; border-color: #e8e8e8; color: #ccc; cursor: not-allowed; text-decoration: line-through; }

.date-prompt {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 20px 0 14px;
  color: #ccc;
  font-size: 12px;
  font-weight: 600;
  text-align: center;
}

.submit-btn {
  width: 100%;
  padding: 11px;
  background: #eac7ff;
  color: #4a1070;
  border: none;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 800;
  cursor: pointer;
  transition: background 0.3s ease, color 0.3s ease, transform 0.1s;
  letter-spacing: 0.01em;
  margin-top: 4px;
  font-family: inherit;
}

.submit-btn:hover:not(:disabled):not(.submit-success) { background: #ba71ff; color: #fff; }
.submit-btn:active { transform: scale(0.98); }
.submit-btn:disabled:not(.submit-success) { background: #f0e0ff; color: #c9a0e0; cursor: not-allowed; }
.submit-btn.submit-success { background: #22c55e !important; color: #fff !important; cursor: not-allowed; transform: none; }

.browse-btn {
  width: 100%;
  padding: 11px;
  background: #fffbe8;
  color: #4a1070;
  border: 1.5px solid #f0e0ff;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 800;
  cursor: pointer;
  transition: background 0.18s, transform 0.1s;
  letter-spacing: 0.01em;
  margin-top: 8px;
  font-family: inherit;
}

.browse-btn:hover { background: #eac7ff; border-color: #ba71ff; }
.browse-btn:active { transform: scale(0.98); }

.message {
  margin-top: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  text-align: center;
}

.message-success { background: #d4f5e2; color: #1a6b3c; }
.message-error { background: #fde8e8; color: #991b1b; }

.booking-notice {
  background: #fffbe8;
  border: 1.5px solid #f6d860;
  border-radius: 11px;
  padding: 10px 14px;
  font-size: 13px;
  font-weight: 600;
  color: #92610a;
  margin-bottom: 14px;
}

.calendar-button-wrapper {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 2000;
}

.calendar-button {
  width: 55px;
  height: 55px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  background: linear-gradient(135deg, #fffbe8, #fff8d3);
  color: #1e3a8a;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  transition: transform 0.2s, background 0.2s, box-shadow 0.2s;
}

.calendar-button:hover {
  transform: scale(1.1);
  background: linear-gradient(135deg, #ffe876, #fffbe8);
  box-shadow: 0 6px 16px rgba(30,58,138,0.25);
}

/* ── Desktop ── */
@media (min-width: 600px) {
  .form-card { padding: 28px 32px; }
  .page-wrapper { padding: 18px 24px 60px; }
  .form-input { padding: 16px 18px; font-size: 16px; border-radius: 14px; }
  .form-label { font-size: 13px; margin-bottom: 8px; }
  .form-group { margin-bottom: 26px; }
  .slots { grid-template-columns: repeat(5, 1fr); gap: 12px; }
  .slot-btn { padding: 14px 8px; font-size: 15px; border-radius: 13px; }
  .submit-btn { padding: 18px; font-size: 17px; margin-top: 12px; }
  .browse-btn { padding: 16px; font-size: 16px; margin-top: 10px; }
  .message { font-size: 14px; padding: 14px 16px; }
}

/* ── Mobile ── */
@media (max-width: 480px) {
  .page-header { padding: 20px 16px 0 46px; }
  .page-wrapper { padding: 12px 12px 24px; }
  .form-card { padding: 12px 14px; }
  .form-group { margin-bottom: 10px; }
  .form-label { font-size: 9px; margin-bottom: 3px; }
  .form-input { padding: 7px 10px; font-size: 12px; border-radius: 8px; }
  .slots { grid-template-columns: repeat(3, 1fr); gap: 6px; }
  .slot-btn { font-size: 11px; padding: 8px 4px; border-radius: 8px; }
  .submit-btn { padding: 10px; font-size: 12px; margin-top: 6px; }
  .browse-btn { padding: 10px; font-size: 12px; margin-top: 6px; }
  .message { font-size: 11px; padding: 8px 10px; }
  .booking-notice { font-size: 11px; padding: 8px 10px; }
}
</style>